import * as CubiqTS from "shared/cubiq-model-multi-asset";

import { OENActivationExpiryMode } from "../enums/oe-activation-expiry-mode.enum";
import { OENLockedState } from "../enums/oe-locked-state.enum";
import { ICNInputControlState } from "../../interface/controls/models/bfx-interface-controls.enums";
import {
  NOSECS_MASK,
  NOSECS_DEFAULT,
  NOSECS_VALIDATE_TIME_PATTERN,
} from "../../interface/controls/bfx-time-input.component";
import { ICFixedCcyModel } from "../../interface/controls/models/bfx-interface-controls.models";
import { OERateDisplayModel } from "./rate/oe-rate-display.model";
import { ConstraintValue } from "../../../../shared/cubiq-services/model/core/ConstraintValue";

export class OEPanelBaseModel {
  public previousValue: {
    rootRefId: string;
    client: CubiqTS.ClientImpl; // ClientImpl
    clientId: string; // string
    inverted: boolean;
    product: string;
    orderType: {
      lastId: CubiqTS.RequestOrderType;
      lastValidId: CubiqTS.RequestOrderType;
    };
  } = {
    rootRefId: null,
    client: null, // ClientImpl
    clientId: null, // string
    inverted: false,
    product: null,
    orderType: {
      lastId: null, // can be null, used for orderType value change detection
      lastValidId: null, // after first assignment should not be null, used for orderType propagation reversal
    },
  };

  /**
   * Market rate descriptor for the currently selected currency pair
   */
  public marketRate: OERateDisplayModel = new OERateDisplayModel();
  /* 	any = {
		bidStr: '', // Most left digits of the market rate
		divider: '/', // Pips or small digits of the value
		offerStr: '', // Most right digit, availble only on precisions of .1 and .5
		/**
		 * Big number representation of the current mid market value.
		 * Left null when no currency pair is selected.
		 *
		bidValue: null,
		offerValue: null,
		invalid: false // Indicated if the value of the displayed market rate invalid (displayed with strikeout)
	}; */

  /***************************************/
  /* CLIENT PANEL */

  public client: {
    value: CubiqTS.ClientImpl; // ClientImpl
    disabled: boolean;
    externalState: ICNInputControlState;
    lockState: OENLockedState;
  } = {
    value: null, // ClientImpl
    disabled: false,
    externalState: null,
    lockState: OENLockedState.UNLOCKED,
  };

  public account: {
    value: CubiqTS.AccountImpl; // AccountImpl
    disabled: boolean;
    externalState: ICNInputControlState;
    lockState: OENLockedState;
    dataset: Array<CubiqTS.AccountImpl>;
  } = {
    value: null, // AccountImpl
    disabled: false,
    externalState: null,
    lockState: OENLockedState.UNLOCKED,
    dataset: [],
  };

  public contacts: {
    label: string;
    value: Array<CubiqTS.ClientContactImpl> | null; // Array<CubiqTS.ClientContactImpl>
    disabled: boolean;
    externalState: any;
    lockState: OENLockedState;
    size: string;
  } = {
    label: "Contacts",
    value: null, // Array<CubiqTS.ClientContactImpl>
    disabled: true,
    externalState: null,
    lockState: OENLockedState.UNLOCKED,
    size: "100%",
  };

  public clientTrader: {
    label: string;
    value: CubiqTS.UserImpl | null; // UserImpl
    disabled: boolean;
    enabled: boolean;
    externalState: any;
    lockState: OENLockedState;
  } = {
    label: "Client Trader",
    value: null, // UserImpl
    disabled: false,
    enabled: false,
    externalState: null,
    lockState: OENLockedState.UNLOCKED,
  };

  public currencyPair: {
    label: string;
    labelKey: string;
    dataset: Array<any>;
    product: string; // Product code (base + nonBase)
    model: CubiqTS.CurrencyPairImpl; //
    inverted: boolean; // Has the inverted pair been selected
    externalState: ICNInputControlState; //  External validation state (BfxAutocompleteStateEnum)
    fwdEnabled: boolean;
    ndfEnabled: boolean;
    mktEnabled: boolean;
    spotEnabled: boolean;
    tslEnabled: boolean;
    lockState: OENLockedState;
    base: string;
    nonBase: string;
  } = {
    label: "Pair",
    labelKey: "order_entry.panel.label.ccy_pair",
    /**
     * An array of all currency pair product codes (strings), contains
     * both inverted and non-inverted values
     */
    dataset: [],
    product: null, // Product code (base + nonBase)
    /**
     * CurrencyPairImpl model for the currently selected pair.
     * Populate whenever product code value changes.
     */
    model: null, //
    inverted: false, // Has the inverted pair been selected
    externalState: null, //  External validation state (BfxAutocompleteStateEnum)
    fwdEnabled: false,
    ndfEnabled: false,
    mktEnabled: false,
    spotEnabled: false,
    tslEnabled: false,
    lockState: OENLockedState.UNLOCKED,

    base: "",
    nonBase: "",
  };

  public orderAmount = {
    label: "Amount",
    labelKey: "order_entry.panel.label.order_amount",
    value: null, // type: BigNumber
    max: new CubiqTS.BigNumber("999999999999999.99"),
    externalState: null,
    lockState: OENLockedState.UNLOCKED,
  };

  public clientIntent: {
    label: string;
    labelKey: string;
    value: CubiqTS.OrderIntent;
    intent: boolean;
    externalState: ICNInputControlState;
    lockState: OENLockedState;
  } = {
    label: "Client intent",
    labelKey: "order_entry.panel.label.client_intent",
    value: CubiqTS.OrderIntent.BUY, // type: CubiqTS.OrderIntent
    intent: false, // buy = false
    externalState: null,
    lockState: OENLockedState.UNLOCKED,
  };

  public fixedCcy: {
    strategyLabel: string;
    strategyLabelKey: string;
    value: ICFixedCcyModel;
    fixedCcy: string;
    ccyPair: string; // length (6)
    lockState: OENLockedState;
  } = {
    strategyLabel: "Strategy Currency",
    strategyLabelKey: "order_entry.panel.label.strategy_ccy",
    value: null,
    fixedCcy: null,
    ccyPair: null,
    lockState: OENLockedState.UNLOCKED,
  };

  public markup: {
    label: string;
    labelKey: string;
    locked: boolean;
    show: boolean;
    value: any; // ICMarkupModel
    disabled: boolean;
    externalState: any;
    criteria: any;
    retrieveEnabled: boolean;
    lockState: OENLockedState;
    isAmendMarkup: boolean;
    config: {
      markupEnabled: boolean;
      markupEntry: boolean;
      markupType: any;
      markupLimitDefault: any;
      markupRetrievalEnabled: boolean;
      markupCheckboxSelected: boolean; // system config: markupLimitCbx, markupAlgoCbx
    };
  } = {
    label: "Markup",
    labelKey: "order_entry.panel.label.markup",
    locked: false,
    show: true,
    value: null, // ICMarkupModel
    disabled: false,
    externalState: null,
    criteria: null,
    retrieveEnabled: false,
    lockState: OENLockedState.UNLOCKED,
    isAmendMarkup: false,
    config: {
      markupEnabled: false,
      markupEntry: false,
      markupType: null,
      markupLimitDefault: null,
      markupRetrievalEnabled: false,
      markupCheckboxSelected: false, // system config: markupLimitCbx, markupAlgoCbx
    },
  };

  public trackingRate: {
    value: CubiqTS.BigNumber;
    textValue: string;
    locked: boolean;
    lockState: OENLockedState;
  } = {
    value: null,
    textValue: "",
    locked: false,
    lockState: OENLockedState.UNLOCKED,
  };

  public clientRate: {
    value: CubiqTS.BigNumber;
    textValue: string;
  } = {
    value: null,
    textValue: "",
  };

  public suspend = {
    show: true,
    showConfig: true,
    value: null, // BfxOrderEntrySuspendModel
    externalState: null,
    lockState: OENLockedState.UNLOCKED,
    from: {
      label: "Suspend From",
      labelKey: "order_entry.suspend.from",
      externalState: null,
      lockState: OENLockedState.UNLOCKED,
    },
    to: {
      label: "Suspend To",
      labelKey: "order_entry.suspend.to",
      externalState: null,
      lockState: OENLockedState.UNLOCKED,
    },
  };

  public goodFrom = {
    label: "From",
    labelKey: "order_entry.good.from",
    tooltip: "Good From",
    tooltipKey: "order_entry.good.from.tooltip",
    mode: OENActivationExpiryMode.ACTIVATION,
    show: false,
    value: null,
    placeholder: "NOW",
    inputMask: NOSECS_MASK,
    inputDefault: NOSECS_DEFAULT,
    inputPattern: NOSECS_VALIDATE_TIME_PATTERN,
    showSeconds: false,
    externalState: null,
    errorMsg: null,
    locked: false, //TODO: To be refactored
    constraintValidFg: true,
    lockState: OENLockedState.UNLOCKED,
  };

  public goodUntil = {
    label: "Until",
    labelKey: "order_entry.good.until",
    tooltip: "Good Until",
    tooltipKey: "order_entry.good.until.tooltip",
    mode: OENActivationExpiryMode.EXPIRY,
    show: false,
    value: null,
    placeholder: "GTC",
    inputMask: NOSECS_MASK,
    inputDefault: NOSECS_DEFAULT,
    inputPattern: NOSECS_VALIDATE_TIME_PATTERN,
    externalState: null,
    errorMsg: null,
    showSeconds: false,
    externalStateOnLockAll: null,
    locked: false, //TODO: To be refactored
    constraintValidFg: true,
    lockState: OENLockedState.UNLOCKED,
  };

  public traderGroup: {
    label: string;
    labelKey: string;
    value: any; // ultimately null | CubiqTS.TradingGroupImpl
    //externalState: null, // not in use, directly assign state
    locked: boolean;
    traderGroupModifiable: boolean;
    lockState: OENLockedState;
    state: ICNInputControlState; //  BfxAutoCompleteComponentState =
    traderGroupVisited: boolean;
    ///
    traderGroups: Array<CubiqTS.TradingGroupImpl>;
    allTraderGroups: Array<CubiqTS.TradingGroupImpl>;
    limitTraderGroups: Array<CubiqTS.TradingGroupImpl>;
    manualTraderGroups: Array<CubiqTS.TradingGroupImpl>;
    benchmarkTraderGroups: Array<CubiqTS.TradingGroupImpl>;
    algoTraderGroups: Array<CubiqTS.TradingGroupImpl>;
    manualVoiceOnlyTraderGroups: Array<CubiqTS.TradingGroupImpl>;
  } = {
    label: "Trader Group",
    labelKey: "order_entry.trader.group",
    value: null, // CubiqTS.TradingGroupImpl
    //externalState: null, // not in use, directly assign state
    locked: false,
    traderGroupModifiable: true,
    lockState: OENLockedState.UNLOCKED,
    state: ICNInputControlState.INACTIVE, //  BfxAutoCompleteComponentState =
    traderGroupVisited: false,
    ///
    traderGroups: [],
    allTraderGroups: [],
    limitTraderGroups: [],
    manualTraderGroups: [],
    benchmarkTraderGroups: [],
    algoTraderGroups: [],
    manualVoiceOnlyTraderGroups: [],
  };

  /***************************************/
  /* MORE */
  public commentPrivate: {
    label: string;
    labelKey: string;
    show: boolean;
    showConfig: boolean;
    value: string;
    externalState: any;
    locked: boolean;
    lockState: OENLockedState;
    uiPrivatized: boolean;
    privatizationLabel: string;
  } = {
    label: "Private Comments",
    labelKey: "order_entry.private.comments",
    show: false,
    showConfig: false,
    value: null,
    externalState: null,
    locked: false, // applied value for lock
    lockState: OENLockedState.UNLOCKED, // lock indicator
    uiPrivatized: false,
    privatizationLabel: null,
  };

  public commentClient: {
    label: string;
    labelKey: string;
    show: boolean;
    value: string;
    externalState: any;
    locked: boolean;
    lockState: OENLockedState;
    checkbox: {
      show: boolean;
      value: boolean;
    };
    uiPrivatized: boolean;
    privatizationLabel: string;
  } = {
    label: "Client Comments",
    labelKey: "order_entry.client.comments",
    show: false,
    value: null, //string
    externalState: null,
    locked: false, // boolean
    lockState: OENLockedState.UNLOCKED,
    checkbox: {
      show: false,
      value: false,
    },
    uiPrivatized: false,
    privatizationLabel: null,
  };

  public holdSTP = {
    // Checkbox
    label: "Hold STP",
    labelKey: "order_entry.holdSTP",
    show: false,
    //	enabled: false,
    enabledConfig: false,
    value: false, // boolean
    //	locked: false,
    lockState: OENLockedState.UNLOCKED,
  };

  public autoFwdPricing = {
    // Checkbox
    label: "Auto Forward Roll",
    labelKey: "order_entry.panel.label.autoFwdPricing",
    show: false,
    value: null,
    enabledConfig: false,
    manualOnly: false,
    lockState: OENLockedState.UNLOCKED,
  };

  public privateOrder: {
    // Checkbox
    label: string;
    labelKey: string;
    show: boolean;
    showConfig: boolean;
    value: boolean;
    lockState: OENLockedState;
    uiPrivatized: boolean;
  } = {
    label: "Private Order",
    labelKey: "order_entry.privateOrder",
    show: false,
    showConfig: false,
    value: false,
    lockState: OENLockedState.UNLOCKED,
    uiPrivatized: false,
  };

  public partialFill = {
    // Checkbox
    label: "Allow partial fills",
    labelKey: "order_entry.panel.label.partial_fills",
    show: false,
    _value: ConstraintValue.None as ConstraintValue,
    get value() {
      return this._value;
    },
    set value(value) {
      this._value = value;
    },
    //	locked: false,
    lockState: OENLockedState.UNLOCKED,
  };

  public stpAgg = {
    // Checkbox
    label: "STP Aggregation",
    labelKey: "order_entry.panel.label.stpAgg",
    show: false,
    _value: ConstraintValue.None as ConstraintValue,
    get value(): ConstraintValue {
      return this._value;
    },
    set value(value: ConstraintValue) {
      this._value = value;
    },
    enabledConfig: false,
    lockState: OENLockedState.UNLOCKED,
  };

  public oboClient: {
    value: string;
    disabled: boolean;
    externalState: ICNInputControlState;
    lockState: OENLockedState;
  } = {
    value: null,
    disabled: false,
    externalState: null,
    lockState: OENLockedState.UNLOCKED,
  };

  public oboAccount: {
    value: string;
    disabled: boolean;
    externalState: ICNInputControlState;
    lockState: OENLockedState;
  } = {
    value: null,
    disabled: false,
    externalState: null,
    lockState: OENLockedState.UNLOCKED,
  };

  public customValues: {
    value: Array<CubiqTS.CustomValue>;
    lockState: OENLockedState;
    locked: boolean;
    externalState: ICNInputControlState;
  } = {
    value: null,
    lockState: OENLockedState.UNLOCKED,
    locked: false,
    externalState: null,
  };
}
